import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Plus, Receipt as ReceiptIcon, Eye, Radio } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useExpertPayouts } from '@/hooks/useExpertPayouts';
import { NewPayoutDialog } from '@/components/admin/payouts/NewPayoutDialog';

function formatMoney(n: number, c: string) {
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: c, maximumFractionDigits: 0 }).format(n);
}

function formatMonth(d: string) {
  const [y, m] = d.split('-');
  const names = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return `${names[Number(m) - 1]} ${y}`;
}

const statusVariant: Record<string, any> = {
  draft: 'secondary', closed: 'default', paid: 'default', cancelled: 'outline',
};
const statusLabel: Record<string, string> = {
  draft: 'Borrador', closed: 'Cerrada', paid: 'Pagada', cancelled: 'Cancelada',
};

export default function PayoutsPage() {
  const { data: payouts = [], isLoading } = useExpertPayouts();
  const [openNew, setOpenNew] = useState(false);

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-bold flex items-center gap-2">
            <ReceiptIcon className="w-7 h-7 text-primary" /> Liquidaciones a Expertos
          </h1>
          <p className="text-sm text-muted-foreground">Genera y administra las liquidaciones mensuales por horas dictadas.</p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline"><Link to="/live-sessions"><Radio className="w-4 h-4" /> Sesiones en Vivo</Link></Button>
          <Button onClick={() => setOpenNew(true)}><Plus className="w-4 h-4" /> Nueva liquidación</Button>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Histórico</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Período</TableHead>
                  <TableHead>Experto</TableHead>
                  <TableHead>Horas</TableHead>
                  <TableHead>Tarifa</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Creada</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Cargando…</TableCell></TableRow>}
                {!isLoading && payouts.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Aún no hay liquidaciones.</TableCell></TableRow>}
                {payouts.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{formatMonth(p.period_month.slice(0, 7))}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="w-7 h-7">
                          <AvatarImage src={p.expert_avatar ?? undefined} />
                          <AvatarFallback>{(p.expert_name ?? '?').slice(0, 1)}</AvatarFallback>
                        </Avatar>
                        <span className="text-sm">{p.expert_name ?? p.expert_user_id.slice(0, 8)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono">{Number(p.total_hours).toFixed(2)}h</TableCell>
                    <TableCell className="font-mono">{formatMoney(Number(p.hourly_rate), p.currency)}/h</TableCell>
                    <TableCell className="font-mono font-semibold">{formatMoney(Number(p.total_amount), p.currency)}</TableCell>
                    <TableCell><Badge variant={statusVariant[p.status]}>{statusLabel[p.status]}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Button asChild size="sm" variant="ghost"><Link to={`/payouts/${p.id}`}><Eye className="w-4 h-4" /></Link></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <NewPayoutDialog open={openNew} onOpenChange={setOpenNew} />
    </div>
  );
}
